import React, { useRef, useEffect } from 'react';
import { Box, Typography, Button, Paper, Grid, Card, CardActionArea } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HouseIcon from '@mui/icons-material/House';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';

import { UserProfile } from '../types';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_1_200, COLOR_PRIMARY_2 } from 'src/utils/colors';

interface QuestionOneProps {
  value?: UserProfile['userType'];
  onChange: (data: { userType: UserProfile['userType']; customDescription?: string }) => void;
  onNext: () => void;
  pageStyle: any;
}

const options = [
  {
    value: 'new_property_investor',
    label: "I'm thinking of starting an Airbnb business",
    icon: <RocketLaunchIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'property_investor',
    label: 'I already manage one or more Airbnb listings',
    icon: <HouseIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'property_manager',
    label: 'I help others start an Airbnb (consultant, realtor, property manager, etc.)',
    icon: <HandshakeIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'unclassified',
    label: 'Other (Please describe)',
    icon: <EditNoteIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
];

const QuestionOne: React.FC<QuestionOneProps> = ({ value, onChange, onNext, pageStyle }) => {
  const radioRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [otherText, setOtherText] = React.useState('');
  const otherInputRef = React.useRef<HTMLDivElement | null>(null);
  const [selectedType, setSelectedType] = React.useState<UserProfile['userType'] | null>(
    value || null
  );

  useEffect(() => {
    if (value === 'unclassified' && otherInputRef.current) {
      otherInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [value]);

  const handleSelect = (selected: UserProfile['userType']) => {
    setError(null);
    if (selected !== 'unclassified') {
      setOtherText('');
      setSelectedType(selected);
      onChange({ userType: selected });
    } else {
      setSelectedType('unclassified');
    }
  };

  const handleNext = () => {
    if (!selectedType) {
      setError('Please select an option to continue.');
      return;
    }
    if (selectedType === 'unclassified' && !otherText.trim()) {
      setError('Please describe.');
      return;
    }
    setError(null);
    if (selectedType === 'unclassified' && otherText.trim()) {
      onChange({ userType: 'unclassified', customDescription: otherText });
    }
    onNext();
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, idx: number) => {
    if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIdx = (idx + 1) % options.length;
      if (radioRefs.current[nextIdx]) {
        radioRefs.current[nextIdx]!.focus();
      }
    } else if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const prevIdx = (idx - 1 + options.length) % options.length;
      if (radioRefs.current[prevIdx]) {
        radioRefs.current[prevIdx]!.focus();
      }
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleSelect(options[idx].value as UserProfile['userType']);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        ...pageStyle,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '@media (max-width:480px)': {
            maxHeight: 'calc(100vh - 120px)',
          },
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mt: { xs: 4, sm: 0 } }}>
          What best describes you?
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box role="radiogroup" aria-label="What best describes you?" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {options.map((option, idx) => (
              <Grid item xs={12} sm={6} key={option.value}>
                <label style={{ width: '100%', display: 'block', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="userType"
                    value={option.value}
                    checked={selectedType === option.value}
                    onChange={() => handleSelect(option.value as UserProfile['userType'])}
                    ref={el => (radioRefs.current[idx] = el)}
                    tabIndex={selectedType === option.value ? 0 : -1}
                    aria-checked={selectedType === option.value}
                    role="radio"
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                    onKeyDown={e => handleKeyDown(e, idx)}
                  />
                  <Card
                    variant={selectedType === option.value ? 'elevation' : 'outlined'}
                    tabIndex={-1}
                    sx={{
                      border:
                        selectedType === option.value
                          ? `2px solid ${COLOR_PRIMARY_1}`
                          : '1px solid #e0e0e0',
                      boxShadow: selectedType === option.value ? 6 : 0,
                      background: selectedType === option.value ? '#f0f7ff' : '#fff',
                      transition: '0.2s',
                      minHeight: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 3,
                      px: 2,
                      position: 'relative',
                      '&:focus-within': {
                        boxShadow: 8,
                      },
                      '@media (max-width:480px)': {
                        minHeight: 0,
                        py: 3,
                      },
                    }}
                    onClick={() => handleSelect(option.value as UserProfile['userType'])}
                  >
                    <CardActionArea
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 0,
                        px: 0,
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        {option.icon}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mt: 1,
                            fontWeight: 500,
                            maxWidth: '30ch',
                            wordBreak: 'break-word',
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </label>
                {/* Show text field if 'Other' is selected */}
                {option.value === 'unclassified' && selectedType === 'unclassified' && (
                  <Box sx={{ mt: 2 }} ref={otherInputRef}>
                    <TextField
                      fullWidth
                      placeholder="Please Describe"
                      value={otherText}
                      onChange={e => {
                        const newValue = e.target.value || 'unclassified';
                        setOtherText(newValue);
                      }}
                      inputProps={{ maxLength: 80 }}
                    />
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          {/* Progress dots: step 1 of 3 */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLOR_PRIMARY_1 }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', pb: { xs: 2, sm: 0 } }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={!selectedType || (selectedType === 'unclassified' && !otherText.trim())}
        >
          Next
        </Button>
      </Box>
    </Paper>
  );
};

export default QuestionOne;
